import { type DatabaseAdapter } from '@databases/selections-api-types';
import { type DatabaseEntry, type DatabaseField } from '@databases/types/database';

/**
 * Generic interface for checking if a plugin/feature is enabled.
 * Implementations should check against their own plugin system.
 */
export interface PluginChecker {
  isEnabled(key: string): boolean;
}

/**
 * Shape of database state as provided by Redux selectors.
 * This matches the structure returned by selectDatabaseByContentIdRequired.
 *
 * Note: `fields` contains view-filtered field IDs, while `fieldsById` contains
 * all fields. Use Object.keys(fieldsById) to get all field IDs.
 */
interface DatabaseSnapshot {
  fields: readonly string[];
  fieldsById: Readonly<Record<string, DatabaseField>>;
  entries: readonly string[];
  entriesById: Readonly<Record<string, DatabaseEntry>>;
  meta: {
    isLocked?: boolean;
  };
  space: {
    key: string;
  };
  operations: {
    updateEntries: boolean;
    updateMetadata: boolean;
    delete: boolean;
  };
}

export interface ReduxDatabaseAdapterOptions {
  getContentId: () => string;
  getDatabaseSnapshot: (contentId: string) => DatabaseSnapshot | undefined;
  getVersionId: () => string | undefined;
  pluginChecker: PluginChecker;
}

/**
 * DatabaseAdapter implementation that reads from Redux state.
 *
 * Uses a snapshot getter pattern to ensure fresh data on each access,
 * allowing the SelectionsApi to work with the current view-filtered state.
 */
export class ReduxDatabaseAdapter implements DatabaseAdapter {
  #getContentId: () => string;
  #getDatabaseSnapshot: (contentId: string) => DatabaseSnapshot | undefined;
  #getVersionId: () => string | undefined;
  #pluginChecker: PluginChecker;

  constructor(options: ReduxDatabaseAdapterOptions) {
    this.#getContentId = options.getContentId;
    this.#getDatabaseSnapshot = options.getDatabaseSnapshot;
    this.#getVersionId = options.getVersionId;
    this.#pluginChecker = options.pluginChecker;
  }

  #getDatabase(): DatabaseSnapshot | undefined {
    return this.#getDatabaseSnapshot(this.#getContentId());
  }

  isLocked(): boolean {
    return this.#getDatabase()?.meta.isLocked ?? false;
  }

  getVisibleFieldIds(): string[] {
    const fields = this.#getDatabase()?.fields;

    return fields ? [...fields] : [];
  }

  getVisibleEntryIds(): string[] {
    const entries = this.#getDatabase()?.entries;

    return entries ? [...entries] : [];
  }

  getFieldById(fieldId: string): DatabaseField | undefined {
    return this.#getDatabase()?.fieldsById[fieldId];
  }

  getEntryById(entryId: string): DatabaseEntry | undefined {
    return this.#getDatabase()?.entriesById[entryId];
  }

  getSpaceKey(): string {
    return this.#getDatabase()?.space.key ?? '';
  }

  getAllFieldIds(): string[] {
    const fieldsById = this.#getDatabase()?.fieldsById;

    return fieldsById ? Object.keys(fieldsById) : [];
  }

  getOperations(): {
    updateEntries: boolean;
    updateMetadata: boolean;
    canDelete: boolean;
  } {
    const database = this.#getDatabase();

    if (database?.operations) {
      return {
        updateEntries: database.operations.updateEntries,
        updateMetadata: database.operations.updateMetadata,
        canDelete: database.operations.delete,
      };
    }

    return {
      updateEntries: false,
      updateMetadata: false,
      canDelete: false,
    };
  }

  /**
   * @see databases/packages/frontend/src/common/components/database-content/DatabaseContent.tsx
   */
  canEditContent(): boolean {
    const operations = this.getOperations();
    const versionId = this.#getVersionId();

    return (
      operations.updateEntries &&
      this.#pluginChecker.isEnabled('edit-content') &&
      !versionId
    );
  }

  /**
   * @see databases/packages/views/src/hooks/useCanEditFields.ts
   */
  canEditFields(): boolean {
    const operations = this.getOperations();
    const isLocked = this.isLocked();
    const versionId = this.#getVersionId();

    return (
      !versionId &&
      operations.updateMetadata &&
      this.#pluginChecker.isEnabled('edit-content') &&
      !isLocked
    );
  }
}
